import { PROJECT_STATUS } from 'modules/work-implementation/constants';
import { formatDate } from '../../../shared/utils/misc';
import { calculateDrawings } from '../typeOfWork.calculations';
import { buildMilestoneRows, computeQuoteTotals } from './jobQuote.mapper';
import {
  GROUND_LEVEL_TYPE,
  QUOTE_AS_PER,
  QUOTE_AS_PER_LABELS,
  WORK_AGE,
  WORK_AGE_LABELS,
  WORK_CATEGORY_LABELS,
} from '../work.constants';

const num = (v) => Number(v || 0);

/**
 * Builds the `POST /api/analyst/job/create` body.
 *
 * @param values   the form tree
 * @param pocIds   `{architect_id, builder_id, owner_id, supervisor_id}` from
 *                 `ensureProjectPocIds` — the endpoint links contacts by id and ignores
 *                 inline contact details, so they are resolved before mapping.
 */
export function mapProjectFormToApiPayload(values, pocIds: Record<string, number | null> = {}) {
  const basicDetailsMapper = {
    job_name: values.projectName,
    job_date: values.projectDate ? formatDate(values.projectDate, 'YYYY-MM-DD') : null,
    site_address: values.siteAddress,
    locality: values.locality,

    basic_details_notes: values.basicDetailsNotes,
  };

  const pocMapper = {
    architect_id: pocIds.architect_id ?? null,
    builder_id: pocIds.builder_id ?? null,
    owner_id: pocIds.owner_id ?? null,
    // V36 — which of the three contacts is the client.
    client_type: values.clientType ?? 'OWNER',
    supervisor_id: pocIds.supervisor_id ?? null,
    poc_notes: values.pocNotes,
  };

  const drawings = calculateDrawings(values);
  const isAllied = values.hasAlliedWork;
  const isBuilding = values.hasBuildingWork;
  const isNewWork = values.workAge === WORK_AGE.NEW;
  const isExistingWork = values.workAge === WORK_AGE.EXISTING;
  const isSingleGroundLevel = values.groundLevelType === GROUND_LEVEL_TYPE.SINGLE;
  const isMultipleGroundLevel = values.groundLevelType === GROUND_LEVEL_TYPE.MULTIPLE;

  const typeOfWorkMapper = {
    job_type: WORK_AGE_LABELS[values.workAge],
    job_work_type: WORK_CATEGORY_LABELS[values.workCategory],
    building_type: isBuilding
      ? values.buildingType === 'OTHERS'
        ? values.buildingTypeOther
        : values.buildingType
      : null,
    basement_count: values.hasBasement ? num(values.basementCount) : 0,
    ground_level_type: values.groundLevelType,
    single_ground_usage: isSingleGroundLevel ? values.singleGroundUsage || 'NONE' : 'NONE',
    single_ground_mezzanine: isSingleGroundLevel ? Boolean(values.singleHasMezzanine) : false,
    lower_ground_usage: isMultipleGroundLevel ? values.lowerGroundUsage || 'NONE' : 'NONE',
    lower_ground_mezzanine: isMultipleGroundLevel ? Boolean(values.lowerGroundHasMezzanine) : false,
    lower_ground_column_footing_required: isMultipleGroundLevel
      ? Boolean(values.lowerGroundRequiresColumnFooting)
      : false,
    upper_ground_usage: isMultipleGroundLevel ? values.upperGroundUsage || 'NONE' : 'NONE',
    upper_ground_mezzanine: isMultipleGroundLevel ? Boolean(values.upperGroundHasMezzanine) : false,
    upper_floor_groups: values.upperFloorGroups,
    upper_floor_count: num(values.upperFloorsCount),
    upper_floor_drawing_count: drawings.upperFloorDrawings || 0,
    additional_floor_count: isNewWork ? Number(values.additionalFloorsCount || 0) : 0,
    // The screen asks for the floor the new work starts at; the count of floors already
    // standing is that less one. Both travel, so nothing reading either has to derive it.
    existing_floor_count: isExistingWork ? drawings.existingFloorDrawings : 0,
    starting_floor_no: isExistingWork ? drawings.startingFloorNo : 0,
    staircase_ohwt_count: isBuilding ? drawings.staircaseOhwtSlabs : 0,
    total_area_sqft: num(values.totalAreaSqFt),
    basement_drawings_count: drawings.basementDrawings,
    ground_floor_drawing_count: drawings.groundLevelDrawings,
    existing_floor_drawing_count: drawings.existingFloorDrawings || 0,
    column_drawings_count: drawings.columnFootingDrawings || 0,
    structural_drawings_count: drawings.structuralDrawings,
    type_of_work_notes: values.typeOfWorkNotes,
  };

  // `discount` is read by computeQuoteTotals from `values` directly, not destructured here.
  const { quote, siteVisit, milestones } = values;

  // One definition of how a quotation adds up, shared with the Edit Job QUOTATION panel
  // via `POST /api/job-quotes` — see jobQuote.mapper. `gstAmount` is computed there and
  // is not sent by this endpoint; the server derives GST itself.
  const {
    totalCost,
    totalAmountSelected,
    constructionCost,
    discountAmount,
    totalAfterDiscount,
    grossTotal,
  } = computeQuoteTotals(values);

  const accountingMapper = {
    total_cost: totalCost,
    total_amount_selected: totalAmountSelected,
    discount: discountAmount,
    total_after_discount: totalAfterDiscount,
    gross_total: grossTotal,
    quote_rate_per_sq_ft: quote.asPer === QUOTE_AS_PER.SQFT ? num(quote.sqft.rate) : 0,
    job_area: quote.asPer !== QUOTE_AS_PER.LUMPSUM ? num(quote.area) : 0,
    construction_cost: constructionCost,
    quote_percent: quote.asPer === QUOTE_AS_PER.PERCENTAGE ? num(quote.percentage.value) : 0,
    site_engineer_visit_fee: num(siteVisit.engineer.charge),
    site_engineer_free_visit_count: num(siteVisit.engineer.freeVisits),
    director_visit_fee: num(siteVisit.director.charge),
    director_free_visit_count: num(siteVisit.director.freeVisits),
    // V18 — read by the quotation PDF. Sent from here too, so a job created by the wizard
    // prints the same letter as one whose quotation came from the QUOTATION panel.
    scope_description: quote.scopeDescription || null,
    site_visit_in_scope: siteVisit.inScope !== false,
    site_visit_expenses: siteVisit.inScope !== false ? siteVisit.expenses || null : null,
    milestone_count: milestones.count,
    milestones_completed: 0,
    // Built by the shared mapper, so a job created here and a job whose milestones are
    // re-saved from the QUOTATION panel store the same rows.
    milestones: buildMilestoneRows(values),
    job_code: values.jobCode,
  };

  /**
   * Secondary attributes, sent as the API's nested `details` block — these are the
   * columns V6 moved from `work_jobs` out to the 1:1 `work_jobs2` table. The form
   * keeps them under a matching `details` branch, so this is a rename-only pass.
   *
   * `quote_as_per` is the exception: it belongs to work_jobs2 but is read from
   * `quote.asPer`, which drives the whole accounting step's calculations.
   */
  const details = values.details ?? {};

  const detailsMapper = {
    sanction_number: details.sanction?.number || null,
    sanction_date: details.sanction?.date ? formatDate(details.sanction.date, 'YYYY-MM-DD') : null,
    sanctioning_authority: mapSanctioningAuthority(details),
    document_reference_number: details.documentReferenceNumber,
    quote_as_per: QUOTE_AS_PER_LABELS[quote.asPer],
    letterhead: details.quotation?.issuedBy || null,
    signature: details.quotation?.signedBy || null,
    allied_work_details: isAllied ? details.alliedWorkDetails : null,
    accounting_notes: details.accountingNotes || null,
  };

  return {
    ...basicDetailsMapper,
    ...pocMapper,
    ...typeOfWorkMapper,
    ...accountingMapper,
    details: detailsMapper,
    job_status: PROJECT_STATUS.NOT_STARTED,
    files: [],
  };
}

// Helpers
function mapSanctioningAuthority(details) {
  const { type, townPlanningOffice, taluka } = details.sanctioningAuthority || {};

  if (!type) return null;

  if (type === 'TOWN_PLANNING') {
    return townPlanningOffice ? `TOWN_PLANNING - ${townPlanningOffice}` : 'TOWN_PLANNING';
  }

  if (type === 'MUNICIPAL') {
    return taluka ? `MUNICIPAL - ${taluka}` : 'MUNICIPAL';
  }

  return type;
}
